/**
 * Response Chat Component
 *
 * Single Responsibility: Presentational component that displays chat messages
 * Dependency Inversion Principle: Depends on abstractions (parsers, processors, renderers)
 */

'use client';

import React from 'react';
import { spacing } from '@/design-system/utils';
import { ChatMessage } from '@/types/chatTypes';
import { MarkdownParser } from './parsers';
import { BlockRenderer } from './renderers';
import { BoldTextProcessor } from './processors';

export interface ResponseChatProps {
  messages: ChatMessage[];
  onPlaceNameClick?: (placeName: string) => void;
}

export const ResponseChat: React.FC<ResponseChatProps> = ({
  messages,
  onPlaceNameClick,
}) => {
  // Track renders for debugging streaming
  const renderCountRef = React.useRef(0);
  const lastContentLengthRef = React.useRef<Record<string, number>>({});

  React.useEffect(() => {
    renderCountRef.current += 1;
    const assistantMessages = messages.filter(msg => msg.role === 'assistant');
    const contentLengths = assistantMessages.reduce(
      (acc, msg) => {
        acc[msg.id] = msg.content?.length || 0;
        return acc;
      },
      {} as Record<string, number>
    );

    const changedMessages = assistantMessages.filter(msg => {
      const lastLength = lastContentLengthRef.current[msg.id] || 0;
      const currentLength = msg.content?.length || 0;
      return lastLength !== currentLength;
    });

    console.log(`🎨 [ResponseChat] Component rendered:`, {
      renderCount: renderCountRef.current,
      totalMessages: messages.length,
      assistantMessages: assistantMessages.length,
      changedMessages: changedMessages.length,
      contentLengths,
      changedMessageIds: changedMessages.map(m => m.id),
      changedContentLengths: changedMessages.map(m => ({
        id: m.id,
        oldLength: lastContentLengthRef.current[m.id] || 0,
        newLength: m.content?.length || 0,
        delta:
          (m.content?.length || 0) - (lastContentLengthRef.current[m.id] || 0),
      })),
      timestamp: new Date().toISOString(),
    });

    lastContentLengthRef.current = contentLengths;
  }, [messages]);

  // Create processor and renderer instances
  // Dependency Injection: Can be swapped for testing or different implementations
  const textProcessor = React.useMemo(() => new BoldTextProcessor(), []);
  const blockRenderer = React.useMemo(
    () => new BlockRenderer(textProcessor),
    [textProcessor]
  );

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[4],
      }}
    >
      {messages
        .filter(message => message.role === 'assistant')
        .map(message => {
          if (!message.content || message.content.trim() === '') {
            return null;
          }

          // Parse markdown into structured blocks
          const blocks = MarkdownParser.parse(message.content);
          const apiLocations = message.locations || [];

          return (
            <div
              key={message.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing[4],
                width: '100%',
              }}
            >
              {blocks.map((block, blockIndex) => {
                const key = `${message.id}-block-${blockIndex}`;

                // Delegate rendering to BlockRenderer
                return blockRenderer.render(block, key, blockIndex, {
                  onPlaceClick: onPlaceNameClick,
                  locations: apiLocations,
                });
              })}
            </div>
          );
        })}
    </div>
  );
};
